<script lang="ts">
  import { CATEGORIES, DEFAULT_PROFILE, getProfile, PROFILES, type Profile, type RuleInfo } from '@gostword/engine';
  import { untrack } from 'svelte';
  import { shareLink } from '../lib/profile';
  import Icon from './Icon.svelte';

  let {
    profile,
    rules,
    hasFile,
    onApply,
    onClose,
  }: { profile: Profile; rules: RuleInfo[]; hasFile: boolean; onApply: (p: Profile) => void; onClose: () => void } = $props();

  type SizeMode = 'min' | '14' | '12' | 'eskd';
  type PageNumbers = `${Profile['pageNumbers']['position']}-${Profile['pageNumbers']['align']}`;

  let d = $state<Profile>(untrack(() => structuredClone(profile)));
  let baseId = $state(untrack(() => profile.id));
  let font = $state('');
  let sizeMode = $state<SizeMode>('min');
  let spacing = $state('1.5');
  let indentRange = $state(false);
  let zeroSpacing = $state(true);
  let dash = $state<'both' | 'em'>('both');
  let pageNumbers = $state<PageNumbers>('bottom-center');
  let upper = $state(true);
  let copied = $state(false);
  let dialog: HTMLDialogElement;

  const base = $derived(getProfile(baseId) ?? DEFAULT_PROFILE);
  const offCount = $derived(rules.filter((r) => d.rules[r.id] === 'off').length);

  /** Включение проверки возвращает важность из стандарта; проверку, выключенную в стандарте, включает с обычной важностью. */
  function toggle(r: RuleInfo, on: boolean): void {
    if (!on) {
      d.rules[r.id] = 'off';
      return;
    }
    const standard = base.rules[r.id];
    if (standard === 'off') d.rules[r.id] = r.severity;
    else if (standard) d.rules[r.id] = standard;
    else delete d.rules[r.id];
  }

  /** Заполняет форму значениями профиля. */
  function fill(p: Profile): void {
    d = structuredClone(p);
    font = p.text.fonts.join(', ');
    sizeMode = p.text.sizePt === 14 ? '14' : p.text.sizePt === 12 ? '12' : p.text.allowedSizesPt?.length ? 'eskd' : 'min';
    spacing = p.text.lineSpacing === 1.5 && p.text.lineSpacingAlt?.includes(2) ? '1.5|2' : String(p.text.lineSpacing);
    indentRange = p.text.firstLineIndentMaxCm !== undefined && p.text.firstLineIndentMaxCm > p.text.firstLineIndentCm;
    zeroSpacing = p.text.spacingAfterPt !== undefined || p.text.spacingBeforePt !== undefined;
    dash = p.captions.dashes.includes('–') ? 'both' : 'em';
    pageNumbers = `${p.pageNumbers.position}-${p.pageNumbers.align}`;
    upper = p.headings.structuralCase === 'upper';
  }
  untrack(() => fill(profile));

  $effect(() => {
    dialog.showModal();
  });

  function build(): Profile {
    const p = $state.snapshot(d) as Profile;
    const fonts = font
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    p.text.fonts = fonts.length ? fonts : ['Times New Roman'];
    p.text.sizePt = sizeMode === '14' || sizeMode === '12' ? Number(sizeMode) : undefined;
    p.text.allowedSizesPt = sizeMode === 'eskd' ? [14, 13, 11] : undefined;
    const [line, alt] = spacing.split('|');
    p.text.lineSpacing = Number(line);
    p.text.lineSpacingAlt = alt ? [Number(alt)] : undefined;
    p.text.firstLineIndentMaxCm = indentRange ? Math.max(1.7, p.text.firstLineIndentCm) : undefined;
    p.text.spacingBeforePt = zeroSpacing ? 0 : undefined;
    p.text.spacingAfterPt = zeroSpacing ? 0 : undefined;
    p.captions.dashes = dash === 'both' ? ['—', '–'] : ['—'];
    const [position, align] = pageNumbers.split('-') as [Profile['pageNumbers']['position'], Profile['pageNumbers']['align']];
    p.pageNumbers.position = position;
    p.pageNumbers.align = align;
    p.headings.structuralCase = upper ? 'upper' : 'sentence';
    return p;
  }

  async function copyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(shareLink(build()));
      copied = true;
      setTimeout(() => (copied = false), 2500);
    } catch {
      prompt('Скопируйте ссылку:', shareLink(build()));
    }
  }
</script>

<dialog
  class="modal wide"
  bind:this={dialog}
  onclose={onClose}
  onclick={(e) => {
    if (e.target === dialog) dialog.close();
  }}
>
  <form
    onsubmit={(e) => {
      e.preventDefault();
      onApply(build());
    }}
  >
    <header class="dlg-head">
      <div>
        <h2>Требования к оформлению</h2>
        <p>Выберите стандарт, на который ссылается ваша методичка. Если она требует иначе, поправьте здесь и поделитесь ссылкой с группой.</p>
      </div>
      <button type="button" class="icon-btn" aria-label="Закрыть" onclick={() => dialog.close()}><Icon name="x" /></button>
    </header>

    <div class="dlg-body">
      <fieldset>
        <legend>Стандарт</legend>
        <div class="standards" role="radiogroup" aria-label="Стандарт">
          {#each PROFILES as p (p.id)}
            <label class="std" class:active={baseId === p.id}>
              <input type="radio" name="standard" value={p.id} bind:group={baseId} onchange={() => fill(base)} />
              <span class="std-name">{p.name}</span>
              <span class="std-desc">{p.description}</span>
            </label>
          {/each}
        </div>
      </fieldset>

      <fieldset>
        <legend>Страница</legend>
        <div class="grid">
          <label>Верхнее поле <span class="unit"><input type="number" min="0" max="80" step="1" bind:value={d.page.margins.top} />мм</span></label>
          <label>Нижнее поле <span class="unit"><input type="number" min="0" max="80" step="1" bind:value={d.page.margins.bottom} />мм</span></label>
          <label>Левое поле <span class="unit"><input type="number" min="0" max="80" step="1" bind:value={d.page.margins.left} />мм</span></label>
          <label>Правое поле <span class="unit"><input type="number" min="0" max="80" step="1" bind:value={d.page.margins.right} />мм</span></label>
        </div>
        <label class="row">
          Номер страницы
          <select bind:value={pageNumbers}>
            <option value="bottom-center">внизу по центру</option>
            <option value="bottom-right">внизу справа</option>
            <option value="top-center">вверху по центру</option>
            <option value="top-right">вверху справа</option>
          </select>
        </label>
      </fieldset>

      <fieldset>
        <legend>Основной текст</legend>
        <label class="row">Шрифт <input type="text" list="font-options" bind:value={font} /></label>
        <datalist id="font-options">
          <option value="Times New Roman"></option>
          <option value="Arial"></option>
          <option value="Times New Roman, Arial"></option>
          <option value="PT Astra Serif"></option>
        </datalist>
        <label class="row">
          Кегль
          <select bind:value={sizeMode}>
            <option value="min">не менее {d.text.minSizePt} пт</option>
            <option value="14">ровно 14 пт</option>
            <option value="12">ровно 12 пт</option>
            <option value="eskd">14, 13 или 11 пт — один на весь текст</option>
          </select>
        </label>
        <label class="row">
          Межстрочный интервал
          <select bind:value={spacing}>
            <option value="1">одинарный</option>
            <option value="1.15">1,15</option>
            <option value="1.5">полуторный</option>
            <option value="1.5|2">полуторный или двойной</option>
            <option value="2">двойной</option>
          </select>
        </label>
        <label class="row">Абзацный отступ <span class="unit"><input type="number" min="0" max="3" step="0.05" bind:value={d.text.firstLineIndentCm} />см</span></label>
        <label class="check"><input type="checkbox" bind:checked={indentRange} /><span>Допускается отступ до 1,7 см</span></label>
        <label class="row">
          Выравнивание
          <select bind:value={d.text.alignment}>
            <option value="justify">по ширине</option>
            <option value="left">по левому краю</option>
            <option value="any">не проверять</option>
          </select>
        </label>
        <label class="check"><input type="checkbox" bind:checked={zeroSpacing} /><span>Интервалы перед абзацем и после — 0 пт</span></label>
      </fieldset>

      <fieldset>
        <legend>Заголовки</legend>
        <label class="check"><input type="checkbox" bind:checked={upper} /><span>Структурные элементы прописными: «ВВЕДЕНИЕ», «СОДЕРЖАНИЕ»</span></label>
        <label class="row">
          Заголовки разделов
          <select bind:value={d.headings.sectionAlign}>
            <option value="indent">с абзацного отступа</option>
            <option value="center">по центру</option>
            <option value="left">от левого края</option>
          </select>
        </label>
        <label class="check"><input type="checkbox" bind:checked={d.headings.numberDot} /><span>Точка после номера раздела: «1. Введение»</span></label>
        <label class="check"><input type="checkbox" bind:checked={d.headings.newPage} /><span>Каждый раздел — с новой страницы</span></label>
      </fieldset>

      <fieldset>
        <legend>Рисунки, таблицы, источники</legend>
        <label class="row">
          Тире в подписях
          <select bind:value={dash}>
            <option value="both">«—» или «–»</option>
            <option value="em">только длинное «—»</option>
          </select>
        </label>
        <label class="row">
          Нумерация рисунков и таблиц
          <select bind:value={d.captions.numbering}>
            <option value="any">любая</option>
            <option value="continuous">сквозная</option>
            <option value="section">в пределах раздела</option>
          </select>
        </label>
        <label class="row">
          Порядок источников
          <select bind:value={d.references.order}>
            <option value="appearance">по первому упоминанию</option>
            <option value="alphabetical">по алфавиту</option>
            <option value="any">не проверять</option>
          </select>
        </label>
      </fieldset>

      {#if rules.length}
        <fieldset>
          <legend>Проверки</legend>
          <details class="rule-list">
            <summary>Включить или выключить отдельные проверки{#if offCount}<span class="off"> · выключено {offCount} из {rules.length}</span>{/if}</summary>
            {#each CATEGORIES as c (c.id)}
              <h3>{c.title}</h3>
              {#each rules.filter((r) => r.category === c.id) as r (r.id)}
                <label class="check">
                  <input type="checkbox" checked={d.rules[r.id] !== 'off'} onchange={(e) => toggle(r, e.currentTarget.checked)} />
                  <span>{r.title}</span>
                </label>
              {/each}
            {/each}
          </details>
        </fieldset>
      {/if}
    </div>

    <footer class="dlg-foot">
      <button type="button" class="link" onclick={() => fill(base)}>Сбросить к {base.name}</button>
      <span class="spacer"></span>
      <button type="button" class="btn" onclick={copyLink}>
        <Icon name={copied ? 'check' : 'link'} size={17} />
        {copied ? 'Ссылка скопирована' : 'Скопировать ссылку'}
      </button>
      <button type="submit" class="btn btn-primary">{hasFile ? 'Перепроверить' : 'Применить'}</button>
    </footer>
  </form>
</dialog>

<style>
  .wide {
    width: min(640px, calc(100vw - 32px));
  }

  fieldset {
    margin: 0 0 18px;
    padding: 0;
    border: 0;
  }

  legend {
    margin-bottom: 10px;
    padding: 0;
    color: var(--text-3);
    font-size: 12px;
    font-weight: 650;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .standards {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .std {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 12px 14px;
    border: 1px solid var(--border);
    border-radius: 12px;
    background: var(--surface);
    cursor: pointer;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;
  }

  .std:hover {
    border-color: var(--border-strong);
  }

  .std.active {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
  }

  .std input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  .std-name {
    font-size: 15px;
    font-weight: 640;
  }

  .std-desc {
    color: var(--text-2);
    font-size: 13px;
    line-height: 1.35;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 10px;
  }

  .grid label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: var(--text-2);
    font-size: 13px;
  }

  .unit {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--text-3);
    font-size: 13px;
  }

  .unit input {
    width: 76px;
  }

  .grid .unit input {
    width: 100%;
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 44px;
    border-bottom: 1px solid var(--border);
    font-size: 14px;
  }

  .row select,
  .row input[type='text'] {
    min-width: 200px;
  }

  .check {
    min-height: 40px;
    align-items: center;
  }

  .check input {
    margin-top: 0;
  }

  .rule-list summary {
    padding: 10px 0;
    font-size: 14px;
    cursor: pointer;
  }

  .rule-list .off {
    color: var(--text-3);
  }

  .rule-list h3 {
    margin: 14px 0 2px;
    color: var(--text-3);
    font-size: 12px;
    font-weight: 650;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .rule-list .check {
    min-height: 34px;
    font-size: 14px;
  }

  .spacer {
    flex: 1;
  }

  @media (max-width: 560px) {
    .standards {
      grid-template-columns: 1fr;
    }

    .grid {
      grid-template-columns: 1fr 1fr;
    }

    .row {
      flex-wrap: wrap;
      padding: 8px 0;
    }

    .row select,
    .row input[type='text'] {
      min-width: 0;
      width: 100%;
    }
  }
</style>
